import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import {
    BABOOR_ENVIRONMENT,
    BaboorEnvironmentConfig,
} from '@baboor/baboor-core'
import { ApiResponse } from '@baboor/common-models'
import { Observable } from 'rxjs'

export interface GoogleConnectionStatus {
    isConnected: boolean
    status: 'connected' | 'disconnected' | 'revoked' | 'error'
    googleAccountId?: string | null
    googleAccountName?: string | null
    locationId?: string | null
    locationName?: string | null
    placeId?: string | null
    googleMapsUrl?: string | null
    lastSyncAt?: string | null
    lastSyncStatus?: 'success' | 'failed' | 'in_progress' | null
    lastSyncError?: string | null
    lastSyncReviewCount?: number
    autoSyncMonthly: boolean
    nextScheduledSyncAt?: string | null
    minimumRating: number
    showOnWebsite: boolean
    totalReviews: number
    averageRating: number
}

export interface GoogleMapsSearchPlaceItem {
    placeId: string
    dataId?: string
    title: string
    address?: string
    rating?: number
    reviewsCount?: number
    thumbnail?: string
    googleMapsUrl?: string
}

export interface ResolveGoogleMapsLinkResponse {
    sourceUrl: string
    canonicalUrl: string
    extractedQuery?: string
    dataId?: string
    placeId?: string
    matchType: 'direct' | 'multiple_candidates' | 'not_found'
    places: GoogleMapsSearchPlaceItem[]
    message?: string
}

export interface ConnectPlaceRequest {
    placeId: string
    placeName: string
    address?: string
    googleMapsUrl?: string
    dataId?: string
}

export interface GoogleLocationItem {
    locationId: string
    locationName: string
    address?: string
    storeCode?: string
    googleAccountId: string
    googleAccountName?: string
}

export interface GoogleReviewItem {
    id: string
    googleReviewId: string
    reviewerName: string
    reviewerPhotoUrl?: string | null
    starRating: number
    comment?: string | null
    reviewReply?: string | null
    reviewReplyTime?: string | null
    reviewUrl?: string | null
    reviewCreateTime: string
    reviewUpdateTime?: string | null
    isPublishedOnWebsite: boolean
    createdAt?: string | null
}

export interface GoogleReviewsListResponse {
    reviews: GoogleReviewItem[]
    total: number
    page: number
    size: number
    averageRating: number
    ratingDistribution: Record<string, number>
}

export interface SyncReviewsResponse {
    syncedCount: number
    message: string
    lastSyncAt: string
}

@Injectable({ providedIn: 'root' })
export class GoogleReviewsApiService {
    private baseUrl: string

    constructor(
        private http: HttpClient,
        @Inject(BABOOR_ENVIRONMENT) private env: BaboorEnvironmentConfig,
    ) {
        this.baseUrl = `${env.apiUrl}/v1/google-reviews`
    }

    getConnectUrl(): Observable<
        ApiResponse<{ url: string; isMockMode?: boolean }>
    > {
        return this.http.get<
            ApiResponse<{ url: string; isMockMode?: boolean }>
        >(`${this.baseUrl}/oauth/connect-url`)
    }

    resolveLink(
        url: string,
        shopId?: string,
    ): Observable<ApiResponse<ResolveGoogleMapsLinkResponse>> {
        const endpoint = shopId
            ? `${this.env.apiUrl}/v1/shops/${shopId}/google-reviews/resolve-link`
            : `${this.baseUrl}/resolve-link`
        return this.http.post<ApiResponse<ResolveGoogleMapsLinkResponse>>(
            endpoint,
            { url },
        )
    }

    searchPlaces(
        query: string,
        shopId?: string,
    ): Observable<ApiResponse<{ places: GoogleMapsSearchPlaceItem[] }>> {
        const params = new HttpParams().set('q', query)
        const endpoint = shopId
            ? `${this.env.apiUrl}/v1/shops/${shopId}/google-reviews/search`
            : `${this.baseUrl}/search-places`
        return this.http.get<
            ApiResponse<{ places: GoogleMapsSearchPlaceItem[] }>
        >(endpoint, { params })
    }

    connectPlace(
        data: ConnectPlaceRequest,
        shopId?: string,
    ): Observable<ApiResponse<{ success: boolean; message: string }>> {
        const endpoint = shopId
            ? `${this.env.apiUrl}/v1/shops/${shopId}/google-reviews/connect`
            : `${this.baseUrl}/connect-place`
        return this.http.post<
            ApiResponse<{ success: boolean; message: string }>
        >(endpoint, data)
    }

    getConnectionStatus(): Observable<ApiResponse<GoogleConnectionStatus>> {
        return this.http.get<ApiResponse<GoogleConnectionStatus>>(
            `${this.baseUrl}/connection`,
        )
    }

    listLocations(): Observable<
        ApiResponse<{ locations: GoogleLocationItem[] }>
    > {
        return this.http.get<ApiResponse<{ locations: GoogleLocationItem[] }>>(
            `${this.baseUrl}/locations`,
        )
    }

    selectLocation(data: {
        locationId: string
        locationName: string
        googleAccountId: string
        googleAccountName?: string
    }): Observable<ApiResponse<Record<string, never>>> {
        return this.http.post<ApiResponse<Record<string, never>>>(
            `${this.baseUrl}/location`,
            data,
        )
    }

    syncReviews(): Observable<ApiResponse<SyncReviewsResponse>> {
        return this.http.post<ApiResponse<SyncReviewsResponse>>(
            `${this.baseUrl}/sync`,
            {},
        )
    }

    updateSettings(data: {
        autoSyncMonthly?: boolean
        minimumRating?: number
        showOnWebsite?: boolean
    }): Observable<ApiResponse<Record<string, never>>> {
        return this.http.patch<ApiResponse<Record<string, never>>>(
            `${this.baseUrl}/settings`,
            data,
        )
    }

    listReviews(
        page = 1,
        size = 20,
        shopId?: string,
    ): Observable<ApiResponse<GoogleReviewsListResponse>> {
        const params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString())

        const endpoint = shopId
            ? `${this.env.apiUrl}/v1/shops/${shopId}/google-reviews`
            : this.baseUrl

        return this.http.get<ApiResponse<GoogleReviewsListResponse>>(endpoint, {
            params,
        })
    }

    toggleReviewPublish(
        reviewId: string,
        isPublishedOnWebsite: boolean,
    ): Observable<ApiResponse<Record<string, never>>> {
        return this.http.patch<ApiResponse<Record<string, never>>>(
            `${this.baseUrl}/${reviewId}/publish`,
            { isPublishedOnWebsite },
        )
    }

    disconnect(): Observable<ApiResponse<Record<string, never>>> {
        return this.http.post<ApiResponse<Record<string, never>>>(
            `${this.baseUrl}/disconnect`,
            {},
        )
    }
}
